import React, {useMemo, useState} from 'react';

import {Heading} from '../Heading';
import {EntityThumbnail} from '../Image';
import {Text} from '../Text';

import {MapCardHoverContainer} from './common';

import type {PointFeature} from 'supercluster';

type NameProps = {
  children: React.ReactNode;
};
export const MapCardName = (props: NameProps): React.JSX.Element => (
  <Heading as="subtitle" tag="h3" className="truncate">
    {props.children}
  </Heading>
);

type ThumbnailProps = Pick<
  React.ComponentProps<typeof EntityThumbnail>,
  'image' | 'icon' | 'iconRaw'
>;
export const MapCardThumbnail = (props: ThumbnailProps): React.JSX.Element => (
  // @ts-expect-error --IGNORE--
  <EntityThumbnail
    {...props}
    className="-m-4 mb-4 h-32 w-[calc(100%+theme(space.8))] overflow-hidden rounded-md"
  />
);

type DetailsProps = {
  details: React.ReactNode[];
};
export const MapCardDetails = (props: DetailsProps): React.JSX.Element => {
  return (
    <div className="space-y-1 [&_p]:whitespace-nowrap">
      {props.details.map((detail, i) => (
        <Text key={i}>{detail}</Text>
      ))}
    </div>
  );
};

type ClusterProps2<T> = {
  items: PointFeature<T>[];
  hover: (idx: number) => React.ReactNode;
  children: (idx: number) => React.ReactNode;
};
export function MapCardCluster2<T extends {id: string}>(
  props: ClusterProps2<T>
): React.JSX.Element {
  const [hovered, setHovered] = useState<number | null>(null);

  const sliced = useMemo(() => {
    return props.items.slice(0, 50);
  }, [props.items]);

  const remaining = props.items.length - sliced.length;

  return (
    <>
      <ul
        className="m-0 list-none p-0 [&>li:last-of-type>a]:border-0 [&>li>a]:block [&>li>a]:border-b [&>li>a]:border-slate6 [&>li>a]:p-2 [&>li>a:hover]:bg-blue10 [&>li>a:hover]:text-white"
        onMouseLeave={() => setHovered(null)}
      >
        {sliced.map((feature, idx) => (
          <li key={feature.properties.id} onMouseEnter={() => setHovered(idx)}>
            {props.children(idx)}
          </li>
        ))}
      </ul>

      {remaining > 0 && <p className="mt-4 font-semibold">+{remaining} more</p>}

      {typeof hovered === 'number' && (
        <MapCardHoverContainer>{props.hover(hovered)}</MapCardHoverContainer>
      )}
    </>
  );
}

type ClusterProps3<T> = {
  items: PointFeature<T>[];
  hover: (item: T) => React.ReactNode;
  children: (item: T) => React.ReactNode;
};
export function MapCardCluster3<T extends {id: string}>(
  props: ClusterProps3<T>
): React.JSX.Element {
  const [hovered, setHovered] = useState<number | null>(null);

  const sliced = useMemo(() => {
    return props.items.slice(0, 50);
  }, [props.items]);

  const remaining = props.items.length - sliced.length;

  return (
    <>
      <ul
        className="m-0 list-none p-0 [&>li:last-of-type>a]:border-0 [&>li>a]:block [&>li>a]:border-b [&>li>a]:border-slate6 [&>li>a]:p-2 [&>li>a:hover]:bg-blue10 [&>li>a:hover]:text-white"
        onMouseLeave={() => setHovered(null)}
      >
        {sliced.map((feature, idx) => (
          <li key={feature.properties.id} onMouseEnter={() => setHovered(idx)}>
            {props.children(feature.properties)}
          </li>
        ))}
      </ul>

      {remaining > 0 && <p className="mt-4 font-bold">+{remaining} more</p>}

      {typeof hovered === 'number' && (
        <MapCardHoverContainer>
          {props.hover(props.items[hovered].properties)}
        </MapCardHoverContainer>
      )}
    </>
  );
}
